import { Loader } from '@10xLabs/react-web/components/Loader'
import { useMutation } from '@apollo/client'
import { IconLayer, LayersList, PolygonLayer } from 'deck.gl'
import { isEmpty, max } from 'lodash'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from 'styled-components'

import Floating from 'components/Floating'
import { LayersIcon } from 'components/Icons/LayersIcon'
import { AngleDownIcon } from 'components/Icons'
import { ColorPicker } from 'features/RoutingProjects/components/ColorPicker'
import { TRGBAColor, TRGBColor } from 'types/maps'
import { Portal } from 'components/Portal'
import { VisibilityOutlineIcon } from 'components/Icons/VisibilityOutlineIcon'
import { VisibilityOffOutlineIcon } from 'components/Icons/VisibilityOffOutlineIcon'
import { useIconAtlas } from 'features/RoutingProjects/hooks'
import { useGoogleMapsZoom } from 'hooks/google-maps'
import { useAllActiveRoutingLayers } from 'features/RoutingLayers/hooks'
import { ILocationsLayerModel, ILocation, IPolygon, IPolygonsLayerModel } from 'features/RoutingLayers/types'
import { CHANGE_ROUTING_LAYER_LOCATIONS_GROUP, CHANGE_ROUTING_LAYER_POLYGONS } from 'features/RoutingLayers/mutations'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canRead } from 'features/Users/utils'
import { useLogin } from 'features/Login/hooks'
import EmptyState from 'components/v2/EmptyState'
import { TrafficIcon } from 'components/Icons/TrafficIcon'
import { PolygonIcon } from 'components/Icons/PolygonIcon'
import { TrafficLayer } from 'components/GoogleMaps/TrafficLayer'

const MapLayersList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 200px;
  overflow: auto;
`

const LayerItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 16px;
`

const LayerLabel = styled.div`
  max-width: 300px;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ColorIndicator = styled.div<{ bgColor: string }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${(props): string => props.bgColor};
  margin-right: 8px;
  flex-shrink: 0;
  cursor: pointer;
`

const LayerLabelContainer = styled.div`
  display: grid;
  grid-template-columns: 16px 1fr;
  align-items: center;
  overflow: hidden;
  gap: 8px;
`

const LayerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const LayerAction = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  height: 29px;
`

const ButtonLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
  font-size: 16px;
  color: #28265d;
`

const LocationLayerColorPickerToolbar = styled(Floating.Toolbar)<{ top: number }>`
  position: relative;
  top: ${(props): string => `-${props.top}px`};
  transform: translateY(100%);
`

interface ISelectMapLayersPanelProps {
  setLayers: (layers: LayersList) => void
  floatingContainer: HTMLDivElement | null
  isMapStyleChanging?: boolean
}

const ITEM_LAYER_COLORPICKER_HEIGHT = 82

export const SelectMapLayersPanel = ({
  setLayers,
  floatingContainer,
  isMapStyleChanging
}: ISelectMapLayersPanelProps): ReactElement | null => {
  const mapLayersRef = useRef<HTMLDivElement | null>(null)
  const zoom = useGoogleMapsZoom()
  const login = useLogin()
  const { iconAtlas, iconMapping } = useIconAtlas()
  const [polygonsLayersState, setPolygonsLayersState] = useState<IPolygonsLayerModel[]>([])
  const [isShowingMapLayers, setIsShowingMapLayers] = useState(false)
  const [showPolygonColorPicker, setShowPolygonColorPicker] = useState(false)
  const [selectedPolygonId, setSelectedPolygonId] = useState<string | null>(null)
  const [colorPickerPosition, setColorPickerPosition] = useState({ x: 0, y: 0, index: -1 })
  const [shownLocationsLayerIds, setShownLocationsLayerIds] = useState<string[]>([])
  const [locationsLayersState, setLocationsLayersState] = useState<ILocationsLayerModel[]>([])
  const [isShowingItemColorPicker, setIsShowingItemColorPicker] = useState(false)
  const [selectedLocationsLayerId, setSelectedLocationsLayerId] = useState<string | null>(null)
  const [itemColorPickerPosition, setItemColorPickerPosition] = useState({ top: 0 })
  const [shownPolygonsLayerIds, setShownPolygonsLayerIds] = useState<string[]>([])
  const [isShowingTrafficLayer, setIsShowingTrafficLayer] = useState(false)

  const { polygonsLayers, locationsLayers, loading: routingLayersLoading } = useAllActiveRoutingLayers()
  const [changeRoutingLayerPolygons] = useMutation(CHANGE_ROUTING_LAYER_POLYGONS, {
    onError: (error) => {
      console.error('Error changing routing layer polygons:', error)
    }
  })
  const [changeRoutingLayerLocationsGroup] = useMutation(CHANGE_ROUTING_LAYER_LOCATIONS_GROUP, {
    onError: (error) => {
      console.error('Error changing routing layer locations group:', error)
    }
  })

  useEffect(() => {
    setPolygonsLayersState([...polygonsLayers])
  }, [polygonsLayers.length])
  useEffect(() => {
    setLocationsLayersState([...locationsLayers])
  }, [locationsLayers.length])

  useEffect(() => {
    if (!isMapStyleChanging) {
      let layers: LayersList = []
      if (!isEmpty(shownPolygonsLayerIds)) {
        shownPolygonsLayerIds.forEach((layerId) => {
          const polygonsLayer = polygonsLayersState.find((item) => item.id === layerId)
          const layer = new PolygonLayer<IPolygon>({
            id: 'polygon-layer',
            data: polygonsLayer?.polygons,
            getPolygon: (d) => d.contour,
            getElevation: 0,
            getFillColor: (d) => d.fillColor,
            getLineColor: (d) => d.lineColor,
            getLineWidth: 20,
            lineWidthMinPixels: 2,
            pickable: true,
            onClick: (info): void => {
              setSelectedPolygonId(info.object.id)
              setColorPickerPosition({ x: info.x, y: info.y, index: info.index })
              setShowPolygonColorPicker(true)
            }
          })
          layers = [layer, ...layers]
        })
      }
      if (!isEmpty(shownLocationsLayerIds) && iconMapping) {
        // Calculate size scale based on zoom level
        // Google Maps zoom ranges from 0 (fully zoomed out) to 22 (fully zoomed in)
        const minZoom = 5
        const maxZoom = 18
        const minScale = 1
        const maxScale = 6

        // Calculate scale based on zoom (inverse relationship - bigger at higher zoom)
        const normalizedZoom = Math.max(minZoom, Math.min(maxZoom, zoom))
        const zoomRatio = (normalizedZoom - minZoom) / (maxZoom - minZoom)
        const sizeScale = minScale + zoomRatio * (maxScale - minScale)

        shownLocationsLayerIds.forEach((locationsLayerId) => {
          const locationsLayer = locationsLayersState.find((locationsLayer) => locationsLayer.id === locationsLayerId)
          if (locationsLayer) {
            const layer = new IconLayer<ILocation>({
              id: `icon-layer-${locationsLayerId}`,
              data: locationsLayer.locationsGroup.locations,
              iconAtlas,
              iconMapping,
              getColor: locationsLayer.locationsGroup.color,
              sizeScale,
              sizeMinPixels: 1,
              sizeMaxPixels: 10,
              getIcon: () => locationsLayer.locationsGroup.icon,
              getPixelOffset: () => [0, 0],
              getPosition: (d) => d.deckCoordinates
            })

            layers = [layer, ...layers]
          }
        })
      }
      setLayers(layers)
      return
    }
    setLayers([])
  }, [
    polygonsLayersState,
    shownPolygonsLayerIds,
    isMapStyleChanging,
    zoom,
    setLayers,
    iconMapping,
    iconAtlas,
    shownLocationsLayerIds,
    locationsLayersState
  ])

  if (!canRead(FEATURES.ROUTING_LAYER, login.accessToken.policies)) {
    return null
  }

  const toggleMapLayers = (): void => {
    setIsShowingMapLayers(!isShowingMapLayers)
    setIsShowingItemColorPicker(false)
  }

  const updatePolygonColor = (color: TRGBColor): void => {
    let modifiedPolygonsLayerId: string | null
    const polygonsLayersCopy = polygonsLayersState.map((polygonsLayer) => {
      return {
        ...polygonsLayer,
        polygons: polygonsLayer.polygons.map((polygon) => {
          if (polygon.id !== selectedPolygonId) {
            return polygon
          }
          modifiedPolygonsLayerId = polygonsLayer.id
          return {
            ...polygon,
            color: [color[0], color[1], color[2]] as TRGBColor,
            fillColor: [color[0], color[1], color[2], polygon.transparency] as TRGBAColor,
            lineColor: [color[0], color[1], color[2], 255] as TRGBAColor
          }
        })
      }
    })
    setPolygonsLayersState(polygonsLayersCopy)

    // Save the modified polygons layer to the backend
    const modifiedPolygonsLayer = polygonsLayersCopy.find((layer) => layer.id === modifiedPolygonsLayerId)
    const modifiedPolygon = modifiedPolygonsLayer?.polygons.find((polygon) => polygon.id === selectedPolygonId)
    if (modifiedPolygonsLayer && modifiedPolygon) {
      changeRoutingLayerPolygons({
        variables: {
          input: {
            id: modifiedPolygonsLayer.id,
            polygons: [
              {
                id: modifiedPolygon.id,
                color: JSON.stringify(modifiedPolygon.color),
                lineWidth: modifiedPolygon.lineWidth,
                transparency: modifiedPolygon.transparency
              }
            ]
          }
        }
      })
    }
  }

  const updateLocationsColor = (color: TRGBColor): void => {
    const locationsLayersCopy = locationsLayersState.map((locationsLayer): ILocationsLayerModel => {
      if (locationsLayer.id === selectedLocationsLayerId) {
        return {
          ...locationsLayer,
          locationsGroup: {
            ...locationsLayer.locationsGroup,
            color: [color[0], color[1], color[2]] as TRGBColor
          }
        }
      }
      return locationsLayer
    })

    setLocationsLayersState(locationsLayersCopy)

    // Save the modified items layer to the backend
    const modifiedLocationsLayer = locationsLayersCopy.find((layer) => layer.id === selectedLocationsLayerId)
    if (modifiedLocationsLayer) {
      changeRoutingLayerLocationsGroup({
        variables: {
          input: {
            id: selectedLocationsLayerId,
            locationsGroupId: modifiedLocationsLayer.locationsGroup.id,
            color: JSON.stringify(modifiedLocationsLayer.locationsGroup.color),
            icon: modifiedLocationsLayer.locationsGroup.icon
          }
        }
      })
    }
  }

  return (
    <>
      <Floating.Toolbar hasSingleItem ref={mapLayersRef}>
        <Floating.ToolbarItem>
          <Floating.ToolbarButton onClick={toggleMapLayers}>
            <ButtonLabel>
              <LayersIcon />
              {isShowingMapLayers && (
                <FormattedMessage id="routingProjects.mapLayers.title" defaultMessage="Map layers" />
              )}
            </ButtonLabel>
            <Floating.ToolbarButtonAngleIcon isCollapsed={!isShowingMapLayers}>
              <AngleDownIcon />
            </Floating.ToolbarButtonAngleIcon>
          </Floating.ToolbarButton>
        </Floating.ToolbarItem>
        {isShowingMapLayers && routingLayersLoading && (
          <EmptyState.Container>
            <Loader />
          </EmptyState.Container>
        )}
        {isShowingMapLayers && !routingLayersLoading && (
          <MapLayersList>
            <LayerItem>
              <LayerLabelContainer>
                <TrafficIcon />
                <LayerLabel>
                  <FormattedMessage id="routingProjects.mapLayers.traffic" defaultMessage="Traffic layer" />
                </LayerLabel>
              </LayerLabelContainer>
              <LayerActions>
                <LayerAction
                  onClick={(): void => {
                    setIsShowingTrafficLayer((prev) => !prev)
                  }}
                >
                  {isShowingTrafficLayer ? <VisibilityOutlineIcon /> : <VisibilityOffOutlineIcon />}
                </LayerAction>
              </LayerActions>
            </LayerItem>
            {isEmpty(polygonsLayers) && isEmpty(locationsLayers) && (
              <LayerItem>
                <LayerLabelContainer>
                  <div />
                  <LayerLabel>
                    <FormattedMessage id="routingProjects.mapLayers.empty" defaultMessage="No layers available" />
                  </LayerLabel>
                </LayerLabelContainer>
              </LayerItem>
            )}
            {polygonsLayers.map((polygonsLayer) => (
              <LayerItem key={polygonsLayer.id}>
                <LayerLabelContainer>
                  <PolygonIcon />
                  <LayerLabel>{polygonsLayer.name}</LayerLabel>
                </LayerLabelContainer>
                <LayerActions>
                  <LayerAction
                    onClick={(): void => {
                      const isLayerVisible = shownPolygonsLayerIds.includes(polygonsLayer.id)
                      if (isLayerVisible) {
                        setShownPolygonsLayerIds(shownPolygonsLayerIds.filter((id) => id !== polygonsLayer.id))
                      } else {
                        setShownPolygonsLayerIds([...shownPolygonsLayerIds, polygonsLayer.id])
                      }
                    }}
                  >
                    {shownPolygonsLayerIds.includes(polygonsLayer.id) ? (
                      <VisibilityOutlineIcon />
                    ) : (
                      <VisibilityOffOutlineIcon />
                    )}
                  </LayerAction>
                </LayerActions>
              </LayerItem>
            ))}
            {locationsLayersState.map((locationsLayer) => (
              <LayerItem key={locationsLayer.id}>
                <LayerLabelContainer>
                  <ColorIndicator
                    bgColor={`rgb(${locationsLayer.locationsGroup.color[0]}, ${locationsLayer.locationsGroup.color[1]}, ${locationsLayer.locationsGroup.color[2]})`}
                    onClick={(e): void => {
                      setSelectedLocationsLayerId(locationsLayer.id)
                      setIsShowingItemColorPicker(true)

                      const rect = e.currentTarget.getBoundingClientRect()
                      const mapLayersRect = mapLayersRef.current?.getBoundingClientRect()

                      // Calculate position relative to the toolbar
                      const relativeTop = mapLayersRect ? mapLayersRect.bottom - rect.top + 8 : rect.top
                      setItemColorPickerPosition({ top: max([ITEM_LAYER_COLORPICKER_HEIGHT, relativeTop]) || 0 })
                    }}
                  />
                  <LayerLabel>{locationsLayer.name}</LayerLabel>
                </LayerLabelContainer>
                <LayerActions>
                  <LayerAction
                    onClick={(): void => {
                      const isItemVisible = shownLocationsLayerIds.includes(locationsLayer.id)
                      if (isItemVisible) {
                        setShownLocationsLayerIds(shownLocationsLayerIds.filter((id) => id !== locationsLayer.id))
                      } else {
                        setShownLocationsLayerIds([...shownLocationsLayerIds, locationsLayer.id])
                      }
                    }}
                  >
                    {shownLocationsLayerIds.includes(locationsLayer.id) ? (
                      <VisibilityOutlineIcon />
                    ) : (
                      <VisibilityOffOutlineIcon />
                    )}
                  </LayerAction>
                </LayerActions>
              </LayerItem>
            ))}
          </MapLayersList>
        )}
      </Floating.Toolbar>
      {isShowingItemColorPicker && (
        <LocationLayerColorPickerToolbar hasSingleItem top={itemColorPickerPosition.top}>
          <ColorPicker
            onClose={(): void => {
              setIsShowingItemColorPicker(false)
              setSelectedLocationsLayerId(null)
            }}
            pickColor={updateLocationsColor}
          />
        </LocationLayerColorPickerToolbar>
      )}
      {showPolygonColorPicker && (
        <Portal container={floatingContainer}>
          <Floating.Content position={colorPickerPosition} center>
            <Floating.Toolbar hasSingleItem>
              <ColorPicker onClose={(): void => setShowPolygonColorPicker(false)} pickColor={updatePolygonColor} />
            </Floating.Toolbar>
          </Floating.Content>
        </Portal>
      )}
      {isShowingTrafficLayer && <TrafficLayer />}
    </>
  )
}
